import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import {
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  AppPageService,
  InitiatorType,
  PromotionInfoData,
  TransactionPreviewResponse,
  MessageService,
  ProductCode
} from "common";
import { ActivatedRoute } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { LoanService } from "../../../loan/loan.service";
import { UserData } from "../../../user/user.model";
import { SimulationTransactionData } from "../../transaction.model";
import { UserService } from "../../../user/user.service";

@UntilDestroy()
@Component({
    selector: 'ifb-transaction-simulation-draw',
    templateUrl: './transaction-simulation-draw.component.html',
    styleUrls: ['./transaction-simulation-draw.component.scss'],
    standalone: false
})
export class TransactionSimulationDrawComponent implements OnInit {
  form: UntypedFormGroup;
  data: SimulationTransactionData;
  currentUser: UserData;
  promotionInfo: PromotionInfoData;

  readonly ProductCode = ProductCode;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly loanService: LoanService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly appPageService: AppPageService,
    private readonly service: UserService,
    private readonly messageService: MessageService,
  ) {
    this.formInit();
  }

  ngOnInit(): void {
    this.service.current
      .pipe(untilDestroyed(this))
      .subscribe((user: UserData | null) => {
        this.currentUser = user;
      });

    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: { userCurrent: UserData, transactionDetails: SimulationTransactionData }) => {
        this.data = data.transactionDetails;
        this.setValidators();
      });

    this.appBarTitleService.title = "Simulate a draw";

    this.form.controls.drawAmount.valueChanges
      .pipe(untilDestroyed(this), debounceTime(500), distinctUntilChanged())
      .subscribe((value: string) => {
        this.data.amountInput = value;
        this.accountNewBalancesInit();
      });

    this.appBarActionsService.invoking
      .pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));
  }

  setAmount(value: number): void {
    this.form.controls.drawAmount.setValue(value);
  }

  close(): void {
    this.appPageService.back();
  }

  applyPromotionChanged(promotion: PromotionInfoData): void {
    this.promotionInfo = promotion;
    if (this.data.amountInput)
      this.accountNewBalancesInit();
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      drawAmount: ['']
    });

    this.setAppBarActions();
  }

  private setValidators(): void {
    const control = this.form.controls.drawAmount;
    control.setValidators(
      [
        Validators.required,
        Validators.min(this.data.amountMinimum),
        Validators.max(this.data.amountMaximum),
      ]);
    control.updateValueAndValidity();
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void =
      this[action.id].bind(this);
    actionHandler(action);
  }

  private accountNewBalancesInit(): void {
    const value = this.data.amountInput
      ? parseFloat(this.data.amountInput)
      : 0;

    this.loanService
      .drawPreview(
        this.data.accountId,
        value,
        InitiatorType.Customer,
        this.currentUser.id.toString(),
        this.promotionInfo?.promoCode)
      .subscribe((res: TransactionPreviewResponse) => {
          // Reassign a new object reference so ifc-transaction-details (which
          // recomputes its list only in ngOnChanges) re-renders. Mutating the
          // existing object in place does not change the @Input reference and
          // leaves the panel stale.
          this.data.accountTransferData = {
            ...this.data.accountTransferData,
            balanceAfter: {
              ...this.data.accountTransferData.balanceAfter,
              outstandingBalance: res.newOutstandingBalance,
              availableBalance: res.newAvailableFunds,
              upcomingPaymentAmount: res.newUpcomingPaymentAmount,
              upcomingPaymentDate: res.newUpcomingPaymentDate,
            },
            fee: res.fee,
            thirdPartyPayoffAmount: res.thirdPartyPayoffAmount,
            disbursedAmount: res.disbursedAmount,
            promotionInfo: res.promotionInfo,
          };
        },
        error => this.messageService.error(error));
  }

  private setAppBarActions(): void {
    this.appBarActionsService.actions = [];
    this.appBarActionsService.actions.push({
      id: "close",
      label: "CLOSE",
      buttonType: "button",
    });
  }
}


